import {useSnackbar} from "../../contexts/SnackbarContext";
import {useCallback, useState} from "react";
import {DataSourceDocumentListResponse, DocumentDataResponse} from "../../models/collections.models";
import axios from "axios";
import {parseError} from "../../errors/http.service";

export interface GroupPaginationState {
    page: number;
    pageSize: number;
    totalPages: number;
    search: string;
}

const INIT_CURRENT_PAGE = 0;
const INIT_PAGE_SIZE = 10;
export const PAGE_SIZE_OPTIONS = [5, 10, 20, 50];

const DEFAULT_PAGINATION_STATE: GroupPaginationState = {
    page: INIT_CURRENT_PAGE,
    pageSize: INIT_PAGE_SIZE,
    totalPages: 0,
    search: "",
};

interface UseGroupDocumentsType {
    itemsByGroup: Record<string, DocumentDataResponse[]>;
    loadingGroupIds: (number | string)[];
    paginationByGroup: Record<string, GroupPaginationState>;
    pageSizeOptions: number[];
    getGroupPagination: (groupId: number) => GroupPaginationState;
    loadGroupItems: (groupId: number, page: number, pageSize: number, search?: string) => Promise<void>;
    handlePageChange: (groupId: number, newPage: number) => void;
    handlePageSizeChange: (groupId: number, newSize: number) => void;
    handleSearchChange: (groupId: number, newSearch: string) => void;
}


const useGroupDocuments = (): UseGroupDocumentsType => {
    const {showSnackbar} = useSnackbar();

    const [itemsByGroup, setItemsByGroup] = useState<Record<string, DocumentDataResponse[]>>({});
    const [loadingGroupIds, setLoadingGroupIds] = useState<(number | string)[]>([]);
    const [paginationByGroup, setPaginationByGroup] = useState<Record<string, GroupPaginationState>>({});

    const getGroupPagination = useCallback((groupId: number): GroupPaginationState => {
        return paginationByGroup[String(groupId)] ?? DEFAULT_PAGINATION_STATE;
    }, [paginationByGroup]);

    const loadGroupItems = useCallback(async (
        groupId: number,
        currentPage: number,
        pageSize: number,
        search = '',
    ) => {
        const key = String(groupId);
        setLoadingGroupIds(prev => prev.includes(groupId) ? prev : [...prev, groupId]);

        try {
            const {data} = await axios.get<DataSourceDocumentListResponse>(`/documents/by-documents-collection/${groupId}`, {
                params: {page: currentPage, size: pageSize, name: search},
            });

            const docs: DocumentDataResponse[] = data?.content ?? [];

            setItemsByGroup(prev => ({...prev, [key]: docs}));
            setPaginationByGroup(prev => ({
                ...prev,
                [key]: {
                    page: data.pageable.pageNumber,
                    pageSize: data.pageable.pageSize,
                    totalPages: data.totalPages,
                    search
                }
            }));

        } catch (e) {
            showSnackbar(parseError(e), 'error');
            console.error(e);
        } finally {
            setLoadingGroupIds(prev => prev.filter(x => x !== groupId));
        }
    }, [showSnackbar]);

    const handlePageChange = useCallback((groupId: number, newPage: number) => {
        const zeroBasedPage = newPage - 1;
        const { pageSize, search } = getGroupPagination(groupId);
        void loadGroupItems(groupId, zeroBasedPage, pageSize, search);
    }, [getGroupPagination, loadGroupItems]);

    const handlePageSizeChange = useCallback((groupId: number, newSize: number) => {
        const { search } = getGroupPagination(groupId);
        void loadGroupItems(groupId, 0, newSize, search);
    }, [getGroupPagination, loadGroupItems]);

    const handleSearchChange = useCallback((groupId: number, newSearch: string) => {
        const { pageSize } = getGroupPagination(groupId);
        void loadGroupItems(groupId, 0, pageSize, newSearch);
    }, [getGroupPagination, loadGroupItems]);

    return {
        itemsByGroup,
        loadingGroupIds,
        paginationByGroup,
        pageSizeOptions: PAGE_SIZE_OPTIONS,
        getGroupPagination,
        loadGroupItems,
        handlePageChange,
        handlePageSizeChange,
        handleSearchChange,
    };
};

export default useGroupDocuments;